import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';

// User interface สำหรับ Backend API
export interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: string;
  picture?: string;
  lastLogin?: string;
  provider?: 'google' | 'facebook' | 'line' | 'demo' | 'local';
  providerId?: string;
  isNewUser?: boolean;
}

// OAuth User interface (จาก Social providers)
export interface OAuthUser {
  id: string;
  name: string;
  email: string;
  picture?: string;
  provider: 'google' | 'facebook' | 'line' | 'demo';
}

// OAuth Login Request สำหรับ Backend
export interface OAuthLoginRequest {
  provider: 'google' | 'facebook' | 'line' | 'demo';
  providerId: string | null;
  email?: string;
  fullName?: string;
  profilePicture?: string;
}

// Auth Response จาก Backend
export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
  };
  message: string;
}

// Login Request
export interface LoginRequest {
  emailOrUsername: string;
  password: string;
}

// Register Request
export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  fullName: string;
}

// Token Storage Keys
const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'currentUser';
const DEMO_USER_ID_KEY = 'demo_user_id';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  
  private apiUrl = `${environment.apiUrl}/auth`;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const userJson = localStorage.getItem(USER_KEY);
    const token = localStorage.getItem(TOKEN_KEY);
    
    if (userJson && token) {
      try {
        const user = JSON.parse(userJson);
        this.currentUserSubject.next(user);
      } catch {
        this.clearStorage();
      }
    }
  }

  /**
   * Login ด้วย email/username และ password
   */
  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, credentials).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.setSession(response.data);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Register user ใหม่
   */
  register(data: RegisterRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register`, data).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Login สำหรับ OAuth providers (Google, Facebook, LINE)
   * ส่งข้อมูลไป Backend เพื่อ find or create user
   */
  loginWithOAuth(oauthUser: OAuthUser): Observable<AuthResponse> {
    const request: OAuthLoginRequest = {
      provider: oauthUser.provider,
      providerId: oauthUser.id,
      email: oauthUser.email,
      fullName: oauthUser.name,
      profilePicture: oauthUser.picture
    };

    return this.http.post<AuthResponse>(`${this.apiUrl}/oauth`, request).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.setSession(response.data);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Login Demo user
   * - ถ้าเครื่องนี้เคยสร้าง demo user แล้ว → ใช้ user เดิม
   * - ถ้ายังไม่เคย → สร้างใหม่และจำไว้ใน localStorage
   */
  loginDemo(): Observable<AuthResponse> {
    // ดึง demo_user_id จาก localStorage (ถ้ามี)
    const existingDemoId = localStorage.getItem(DEMO_USER_ID_KEY);
    
    const request: OAuthLoginRequest = {
      provider: 'demo',
      providerId: existingDemoId  // null ถ้ายังไม่เคยสร้าง
    };

    return this.http.post<AuthResponse>(`${this.apiUrl}/oauth`, request).pipe(
      tap(response => {
        if (response.success && response.data) {
          // เก็บ demo_user_id เพื่อใช้ครั้งต่อไป
          if (response.data.user.providerId) {
            localStorage.setItem(DEMO_USER_ID_KEY, response.data.user.providerId);
          }
          this.setSession(response.data);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Logout และ clear session
   */
  logout(): Observable<any> {
    const token = this.getAccessToken();
    
    // ถ้ามี token ให้เรียก API logout
    if (token) {
      return this.http.post(`${this.apiUrl}/logout`, {}).pipe(
        tap(() => this.clearSession()),
        catchError(err => {
          // ถ้า logout API fail ก็ clear session อยู่ดี
          this.clearSession();
          return of(null);
        })
      );
    }
    
    this.clearSession();
    return of(null);
  }

  /**
   * Logout แบบ sync (ไม่รอ API)
   */
  logoutSync(): void {
    this.clearSession();
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<AuthResponse>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      tap(response => {
        if (response.success && response.data) {
          localStorage.setItem(TOKEN_KEY, response.data.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
        }
      }),
      catchError(err => {
        this.clearSession();
        return throwError(() => err);
      })
    );
  }

  /**
   * Get current user from API
   */
  getCurrentUserFromApi(): Observable<User> {
    return this.http.get<any>(`${this.apiUrl}/me`).pipe(
      map(response => response.data.user),
      tap(user => {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        this.currentUserSubject.next(user);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    const token = this.getAccessToken();
    
    // ถ้าเป็น OAuth/Demo user ไม่ต้องมี token
    if (user && (user.provider === 'google' || user.provider === 'facebook' || user.provider === 'demo')) {
      return true;
    }
    
    // สำหรับ local user ต้องมี token
    return user !== null && token !== null;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiry;
    } catch {
      return true;
    }
  }

  // Private methods
  private setSession(authData: { user: User; accessToken: string; refreshToken: string }): void {
    localStorage.setItem(TOKEN_KEY, authData.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, authData.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authData.user));
    this.currentUserSubject.next(authData.user);
  }

  private clearSession(): void {
    console.log('🧹 Clearing session and localStorage');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(DEMO_USER_ID_KEY);
    this.currentUserSubject.next(null);
    console.log('✅ Session cleared');
  }

  private clearStorage(): void {
    this.clearSession();
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
    
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 401) {
      errorMessage = 'ข้อมูลเข้าสู่ระบบไม่ถูกต้อง';
    } else if (error.status === 409) {
      errorMessage = 'ชื่อผู้ใช้หรืออีเมลนี้มีอยู่ในระบบแล้ว';
    } else if (error.status === 0) {
      errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
    }
    
    return throwError(() => new Error(errorMessage));
  };
}
